import { ChangeEvent, ReactNode, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DataTable, DataTablePageParams, DataTableRowClickEventParams } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { createUrlParams } from "../../../api/client"
import { AddressSimple } from "../../../api/types/address"
import { storage } from "../../../common/storage"
import { useAddressBookList } from "../../../api/queries/address-book/useAddressBookList"
import { useDebounce } from "../../../common/useDebounce"
import { InputText } from "primereact/inputtext"
import { countryColumnTemplate } from "../../common/country"

export default function AddressBookList() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const rowClassName = (address: AddressSimple) => ({
    "cursor-pointer": true,
  })

  const offsetParam: string = searchParams.get("offset") || ""
  const limitParam: string = searchParams.get("limit") || ""
  const fFulltextParam: string = searchParams.get("fFulltext") || ""
  const [offset, setOffset] = useState<number>(parseInt(offsetParam) || 0)
  const [limit, setLimit] = useState<number>(parseInt(limitParam) || storage.get("limit") || 20)

  const [fFulltext, setFFultext] = useState<string>(fFulltextParam)
  const fFulltextDebounced = useDebounce(fFulltext, 500)
  const handleFFultextChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setFFultext(event.target.value)
  const tHeader = <InputText value={fFulltext} onChange={handleFFultextChange}
    placeholder={"Vyhledat"} className={"w-full"} />

  const { data: addresses, isLoading } = useAddressBookList({
    limit,
    offset,
    fFulltext: fFulltextDebounced,
  })

  useEffect(() => {
    navigate({
      search: createUrlParams({ limit, offset, fFulltext: fFulltextDebounced }),
    }, {
      replace: true,
    })
  }, [limit, offset, navigate, fFulltextDebounced])

  const onPage = ({ first, rows }: DataTablePageParams): void => {
    setOffset(first)
    setLimit(rows)
    storage.set("limit", rows)
  }

  const onNewAddressClick = () => navigate("/address-book/new")

  const handleRowClick = (row: DataTableRowClickEventParams) => {
    navigate(`/address-book/${row.data.uuid}`)
  }

  const tName = (row: AddressSimple): string => row.name

  const tStreet = (row: AddressSimple): string => row.street

  const tZip = (row: AddressSimple): string => row.zip || ""

  const tStreetDetails = (row: AddressSimple): string => row.streetDetails || ""

  const tCity = (row: AddressSimple): string => row.city

  const tCountry = (row: AddressSimple): ReactNode => countryColumnTemplate(row.country)

  const tPerson = (row: AddressSimple): string => row.person || ""

  return (
    <>
      <div className={"col-12"}>
        <Button label={"Nová adresa"} icon={"pi pi-plus"} onClick={onNewAddressClick}
          className={"p-button p-button-primary p-button-sm"} />
      </div>

      <div className={"col-12"}>
        <DataTable
          emptyMessage={"Žádné záznamy"} className={"p-datatable-sm"}
          paginator={true} lazy={true} rows={limit} first={offset}
          totalRecords={addresses?.results.totalCount}
          loading={isLoading} onPage={onPage} value={addresses?.results.addressBookList || []}
          onRowClick={handleRowClick} rowClassName={rowClassName} rowHover={true} stripedRows={true}
          paginatorTemplate={"FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
          rowsPerPageOptions={[20, 50, 100]}
          header={tHeader}
        >
          <Column header={"Název"} body={tName} />

          <Column header={"Upřesnění"} body={tStreetDetails} />

          <Column header={"Ulice a číslo domu"} body={tStreet} />

          <Column header={"PSČ"} body={tZip} />

          <Column header={"Město"} body={tCity} />

          <Column header={"Stát"} body={tCountry} />

          <Column header={"Jméno kontaktní osoby"} body={tPerson} />
        </DataTable>
      </div>
    </>
  )
}
